import {
  getMediaField,
  getMediaFieldBySemantic,
  getMediaFieldValue,
  setMediaFieldValue,
} from "./utils"
import bigDecimal from "js-big-decimal"
import { ChangedPlanRecord } from "../../types"
import { calculationContext } from "./calculations"
import { MediaField } from "routes/Base/Admin/GlobalMediaTypes/types"
import { DEFAULT_PRECISION } from "providers/A12n"

const getPopulation = (
  targetType: string,
  populations?: Record<"targetA" | "targetB", number | undefined>,
) => populations?.[`target${targetType}` as keyof typeof populations]

export function calculateImps(
  li: ChangedPlanRecord,
  populations?: Record<"targetA" | "targetB", number | undefined>,
  metricsConst?: string,
): ChangedPlanRecord {
  const cc = calculationContext
  const fields = li[cc.fieldsKey] as ChangedPlanRecord[typeof cc.fieldsKey]

  const grpAField = getMediaField("grpA")
  const grpBField = getMediaField("grpB")

  const populationA = getPopulation("A", populations)
  const populationB = getPopulation("B", populations)

  const impressionsAField = getMediaField("impressionsA")
  const impressionsBField = getMediaField("impressionsB")

  const grpAValue = getMediaFieldValue<number>(fields, grpAField)
  const grpBValue = getMediaFieldValue<number>(fields, grpBField)

  if (!!populationA && !!impressionsAField && metricsConst === "grpA") {
    const impressionsA = bigDecimal.divide(
      bigDecimal.multiply(grpAValue, populationA),
      100,
    )
    const withImpressionsAPrice = setMediaFieldValue(
      !!grpAValue
        ? bigDecimal.round(
            impressionsA,
            DEFAULT_PRECISION,
            bigDecimal.RoundingModes.HALF_EVEN,
          )
        : undefined,
      fields,
      impressionsAField,
    )
    return {
      ...li,
      [cc.fieldsKey]: withImpressionsAPrice,
    }
  }

  if (!!populationB && !!impressionsBField && metricsConst === "grpB") {
    const impressionsB = bigDecimal.divide(
      bigDecimal.multiply(grpBValue, populationB),
      100,
    )
    const withImpressionsBPrice = setMediaFieldValue(
      !!grpBValue
        ? bigDecimal.round(
            impressionsB,
            DEFAULT_PRECISION,
            bigDecimal.RoundingModes.HALF_EVEN,
          )
        : undefined,
      fields,
      impressionsBField,
    )
    return {
      ...li,
      [cc.fieldsKey]: withImpressionsBPrice,
    }
  }

  return li
}

export function calculateGrps(
  li: ChangedPlanRecord,
  precision: number,
  populations?: Record<"targetA" | "targetB", number | undefined>,
  metricsConst?: string,
): ChangedPlanRecord {
  const cc = calculationContext
  const fields = li[cc.fieldsKey] as ChangedPlanRecord[typeof cc.fieldsKey]

  const impressionsAField = getMediaFieldBySemantic("impressionsA")
  const impressionsBField = getMediaFieldBySemantic("impressionsB")

  const populationA = getPopulation("A", populations)
  const populationB = getPopulation("B", populations)

  const grpAField = getMediaField("grpA")
  const grpBField = getMediaField("grpB")

  const impressionsAValue = getMediaFieldValue<number>(
    fields,
    impressionsAField,
  )
  const impressionsBValue = getMediaFieldValue<number>(
    fields,
    impressionsBField,
  )

  if (!!populationA && !!grpAField && metricsConst === "impressionsA") {
    const grpA = bigDecimal.multiply(
      bigDecimal.divide(impressionsAValue, populationA, precision),
      100,
    )
    const withGrpAPrice = setMediaFieldValue(
      !!impressionsAValue
        ? bigDecimal.round(grpA, precision, bigDecimal.RoundingModes.HALF_EVEN)
        : undefined,
      fields,
      grpAField,
    )
    return { ...li, [cc.fieldsKey]: withGrpAPrice }
  }

  if (!!populationB && !!grpBField && metricsConst === "impressionsB") {
    const grpB = bigDecimal.multiply(
      bigDecimal.divide(impressionsBValue, populationB, precision),
      100,
    )
    const withGrpBPrice = setMediaFieldValue(
      !!impressionsBValue
        ? bigDecimal.round(grpB, precision, bigDecimal.RoundingModes.HALF_EVEN)
        : undefined,
      fields,
      grpBField,
    )
    return { ...li, [cc.fieldsKey]: withGrpBPrice }
  }

  return li
}

export function calculateCpps(li: ChangedPlanRecord, precision: number) {
  const cc = calculationContext
  const fields = li[cc.fieldsKey] as ChangedPlanRecord[typeof cc.fieldsKey]

  const cppAField = getMediaField("cppA")
  const cppBField = getMediaField("cppB")
  const grpAField = getMediaField("grpA")
  const grpAValue = getMediaFieldValue<number>(fields, grpAField)
  const grpBField = getMediaField("grpB")
  const grpBValue = getMediaFieldValue<number>(fields, grpBField)
  const spots = getMediaField("spots")
  const spotsCount: number = getMediaFieldValue(fields, spots) as number
  const withCppAPrice = assignCost(
    li,
    "cpp",
    cppAField,
    spotsCount && grpAValue
      ? bigDecimal.multiply(grpAValue, spotsCount)
      : grpAValue?.toString(),
    precision,
  )
  const withCppBPrice = assignCost(
    withCppAPrice,
    "cpp",
    cppBField,
    spotsCount && grpBValue
      ? bigDecimal.multiply(grpBValue, spotsCount)
      : grpBValue?.toString(),
    precision,
  )
  return withCppBPrice
}

export function calculateCpms(li: ChangedPlanRecord, precision: number) {
  const cc = calculationContext
  const fields = li[cc.fieldsKey] as ChangedPlanRecord[typeof cc.fieldsKey]

  const cpmAField = getMediaField("cpmA")
  const cpmBField = getMediaField("cpmB")
  const impressionsAField = getMediaField("impressionsA")
  const impressionsAValue = getMediaFieldValue<number>(
    fields,
    impressionsAField,
  )
  const spots = getMediaField("spots")
  const spotsCount: number = getMediaFieldValue(fields, spots) as number
  const impressionsBField = getMediaField("impressionsB")
  const impressionsBValue = getMediaFieldValue<number>(
    fields,
    impressionsBField,
  )
  const withCpmAPrice = assignCost(
    li,
    "cpm",
    cpmAField,
    spotsCount && impressionsAValue
      ? bigDecimal.multiply(impressionsAValue, spotsCount)
      : impressionsAValue?.toString(),
    precision,
  )

  const withCpmBPrice = assignCost(
    withCpmAPrice,
    "cpm",
    cpmBField,
    spotsCount && impressionsBValue
      ? bigDecimal.multiply(impressionsBValue, spotsCount)
      : impressionsBValue?.toString(),
    precision,
  )
  return withCpmBPrice
}

function assignCost(
  li: ChangedPlanRecord,
  metric: string,
  costMetricField?: MediaField,
  audienceMetricValue?: string,
  precision: number = DEFAULT_PRECISION,
): ChangedPlanRecord {
  const cc = calculationContext
  const fields = li[cc.fieldsKey] as ChangedPlanRecord[typeof cc.fieldsKey]
  if (!audienceMetricValue) return li

  switch (cc.budgetPlanning) {
    case "GROSS_COST": {
      const grossCostField = getMediaFieldBySemantic("grossCost")
      const grossCostValue = getMediaFieldValue<number>(fields, grossCostField)
      if (!grossCostField || !costMetricField || !grossCostValue) return li

      const cpp = bigDecimal.divide(grossCostValue, audienceMetricValue)
      const withCpp = setMediaFieldValue(
        bigDecimal.round(cpp, precision, bigDecimal.RoundingModes.HALF_EVEN),
        fields,
        costMetricField,
      )

      const cpm = bigDecimal.divide(
        bigDecimal.multiply(grossCostValue, 1000),
        audienceMetricValue,
        precision,
      )
      const withCpm = setMediaFieldValue(
        bigDecimal.round(cpm, precision, bigDecimal.RoundingModes.HALF_EVEN),
        fields,
        costMetricField,
      )

      const withAudienceMetric = metric === "cpp" ? withCpp : withCpm

      return { ...li, [cc.fieldsKey]: withAudienceMetric }
    }
    case "NET_COST": {
      const netCostField = getMediaFieldBySemantic("netCost")
      const netCostValue = getMediaFieldValue<number>(fields, netCostField)
      if (!netCostField || !costMetricField || netCostValue === undefined)
        return li

      const cpp = bigDecimal.divide(netCostValue, audienceMetricValue)
      const withCpp = setMediaFieldValue(
        bigDecimal.round(cpp, precision, bigDecimal.RoundingModes.HALF_EVEN),
        fields,
        costMetricField,
      )

      const cpm = bigDecimal.divide(
        bigDecimal.multiply(netCostValue, 1000),
        audienceMetricValue,
        precision,
      )
      const withCpm = setMediaFieldValue(
        bigDecimal.round(cpm, precision, bigDecimal.RoundingModes.HALF_EVEN),
        fields,
        costMetricField,
      )

      const withAudienceMetric = metric === "cpp" ? withCpp : withCpm

      return { ...li, [cc.fieldsKey]: withAudienceMetric }
    }
    default: {
      const billableField = getMediaFieldBySemantic("billable")
      const billableValue = getMediaFieldValue<number>(fields, billableField)
      if (!billableField || !costMetricField || !billableValue) return li

      const cpp = bigDecimal.divide(billableValue, audienceMetricValue)
      const withCpp = setMediaFieldValue(
        bigDecimal.round(cpp, precision, bigDecimal.RoundingModes.HALF_EVEN),
        fields,
        costMetricField,
      )

      const cpm = bigDecimal.divide(
        bigDecimal.multiply(billableValue, 1000),
        audienceMetricValue,
        precision,
      )
      const withCpm = setMediaFieldValue(
        bigDecimal.round(cpm, precision, bigDecimal.RoundingModes.HALF_EVEN),
        fields,
        costMetricField,
      )

      const withAudienceMetric = metric === "cpp" ? withCpp : withCpm

      return { ...li, [cc.fieldsKey]: withAudienceMetric }
    }
  }
}
